// Assembles the plain-data payload the PDF document renders from — a snapshot
// of what's on screen (active view, metric/chart slots, deepdive table) plus
// the Share drawer's white-label settings. Pure given its inputs so it's unit
// testable; the store snapshot is passed in, never read here.
import moment from 'moment'
import { METRIC_BY_ID, METRIC_IDS, type KeyMetric } from '../key-metrics-data'
import type { GridPdfSnapshot } from '../dimension-grid'
import { cardsPdfSnapshot } from './card-snapshot'

/** Printed wherever the live data didn't supply a value. */
const NO_VALUE = '–'

export interface PdfMetric {
	label: string
	value: string
	change: string
	direction?: 'up' | 'down'
	trend: string
}
export interface PdfChart {
	id: string
	title: string
	png: string | null
}
export interface PdfTableRow {
	depth: number
	/** cells[0] is the dimension value label; the rest align to `columns`. */
	cells: string[]
}
export interface PdfTable {
	dimensionLabel: string
	columns: string[]
	rows: PdfTableRow[]
}
export interface ReportPdfData {
	title: string
	dateRange: string
	generatedAt: string
	whiteLabel: boolean
	logoDataUri: string | null
	brandColor: string
	metricSection: string
	chartSection: string
	/** Report byline: 'ClickGuard' normally; null under white-label (no byline —
	 *  the report speaks in a neutral voice so it works for the agency or their
	 *  client alike). */
	preparedBy: string | null
	/** e.g. 'All ad accounts · All campaigns' / 'Google Ads (Main) · 3 campaigns'. */
	scopeLine: string
	/** Executive summary paragraph. */
	summary: string
	/** Up to 3 derived callouts; fewer when data is missing. */
	findings: string[]
	glossary: { term: string; definition: string }[]
	metrics: PdfMetric[]
	charts: PdfChart[]
	table: PdfTable
}

/** Structural subset of the layout store — getState() assigns to it directly. */
export interface LayoutSnapshot {
	activeViewId: string | null
	savedViews: Array<{ id: string; label: string }>
	metricOrder: string[]
	hiddenMetrics: string[]
	// Per-tile metric override (the tile metric picker). A tile whose id is `x`
	// may display metricConfig[x].metric instead of x's own default metric.
	metricConfig?: Record<string, { metric?: string }>
	chartOrder: string[]
	hiddenCharts: string[]
	chartConfig: Record<string, { metric: string; dimension: string; dimension2?: string; chartType?: string; stack100?: boolean }>
	sectionNames: { metric: string; chart: string }
}

// Mirrors charts-row's PANEL_IDS (kept as a local literal for the same reason
// the store mirrors KEY_METRIC_IDS: importing the component module here would
// drag framer/DS into the payload builder and risk require cycles).
export const CHART_PANEL_IDS = ['ip', 'veracity', 'time'] as const

export const visibleChartIds = (
	s: Pick<LayoutSnapshot, 'chartOrder' | 'hiddenCharts'>
): string[] =>
	(s.chartOrder.length ? s.chartOrder : [...CHART_PANEL_IDS]).filter(
		(id) => (CHART_PANEL_IDS as readonly string[]).includes(id) && !s.hiddenCharts.includes(id)
	)

interface ScopeGroup {
	id: string
	name: string
}
interface ReportScope {
	accounts: ScopeGroup[]
	selectedAccountIds: string[]
	campaigns: ScopeGroup[]
	selectedCampaignIds: string[]
}

// Prose em dashes become ', '; a cell that IS just an em dash (the grid's
// "no data at this granularity" marker, e.g. geo Prevented ad waste) is a
// value, not prose — it prints as a plain hyphen, never a bare comma.
const cleanReportText = (text: string): string => {
	const t = text.trim()
	if (t === '—') return '-'
	return t.replace(/\s*—\s*/g, ', ').trim()
}

// 'All <noun>' when everything (or nothing) is selected, the item's own name
// when exactly one is picked, else a plain count — a strict subset.
const scopePart = (selectedIds: string[], all: ScopeGroup[], noun: string): string => {
	const knownIds = new Set(all.map((item) => item.id))
	const selected = [...new Set(selectedIds)].filter((id) => knownIds.has(id))
	if (!all.length || selected.length === 0 || selected.length >= knownIds.size) return `All ${noun}`
	if (selected.length === 1) {
		const match = all.find((a) => a.id === selected[0])
		if (match?.name.trim()) return cleanReportText(match.name)
	}
	return `${selected.length} ${noun}`
}

const buildScopeLine = (scope: ReportScope): string =>
	[
		scopePart(scope.selectedAccountIds, scope.accounts, 'ad accounts'),
		scopePart(scope.selectedCampaignIds, scope.campaigns, 'campaigns')
	].join(' · ')

// ── Exec summary ────────────────────────────────────────────────────────────
type SummaryMetric = { value: string }
type SummaryPool = Partial<
	Record<'Analyzed ad clicks' | 'Bad ad clicks' | 'Blocked sources' | 'Prevented ad waste', SummaryMetric>
>

const joinClauses = (parts: string[]): string => {
	if (parts.length <= 1) return parts.join('')
	if (parts.length === 2) return `${parts[0]} and ${parts[1]}`
	return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}

/** Pure summary-line builder; production code calls it with visible report
 *  metrics, and tests can pass a fabricated pool. `actor` is
 *  who the sentence credits: 'ClickGuard' normally, 'the system' on a
 *  white-label report — neutral rather than the brand, so the copy works
 *  whether the reader is the agency or the agency's client. */
export const buildSummaryLine = (
	startLabel: string,
	endLabel: string,
	pool: SummaryPool,
	actor = 'ClickGuard'
): string => {
	const prevented = pool['Prevented ad waste']
	// Preferred phrasing (Esteban 2026-07-22): lead with the protection OUTCOME
	// (sources blocked from coming back), not the detection count. Used whenever
	// the Blocked sources metric is visible; the clause-composition below stays
	// as the fallback for views that hide it.
	const blocked = pool['Blocked sources']
	if (blocked) {
		const tail = prevented
			? `, preventing an estimated ${prevented.value} in wasted ad spend`
			: ''
		return `Between ${startLabel} and ${endLabel}, ${actor} blocked ${blocked.value} malicious sources from seeing your ads again${tail}.`
	}
	const parts: string[] = []
	const analyzed = pool['Analyzed ad clicks']
	if (analyzed) parts.push(`analyzed ${analyzed.value} ad clicks`)
	const bad = pool['Bad ad clicks']
	// "ad clicks" stays in the clause so it reads correctly on its own — a
	// bad-clicks-only summary must not dangle as "flagged 13,705 as invalid".
	if (bad) parts.push(`flagged ${bad.value} ad clicks as invalid`)
	if (prevented) parts.push(`prevented an estimated ${prevented.value} in wasted ad spend`)
	return parts.length
		? `Between ${startLabel} and ${endLabel}, ${actor} ${joinClauses(parts)}.`
		: `This report covers ${startLabel} through ${endLabel}.`
}

/** Rewrites vendor references in reused on-page copy to a neutral subject
 *  ("ClickGuard's fraud checks" → "the system's fraud checks"). */
export const neutralizeVendor = (text: string): string =>
	cleanReportText(text.replace(/ClickGuard[’']s/g, 'the system’s').replace(/ClickGuard/g, 'the system'))

// ── Findings ─────────────────────────────────────────────────────────────────
// All finders read the real `findingRows` (raw clicks / bad clicks / bad rate)
// that the live grid and the render route publish, so the copy quotes real
// numbers. Rows below the threshold are skipped rather than described.
type FindingRow = { label: string; clicks: number; badClicks: number; badRate: number }

export const highestRateFindingRow = (rows: FindingRow[]): FindingRow | null => {
	const totalClicks = rows.reduce((sum, r) => sum + Math.max(0, r.clicks), 0)
	const threshold = Math.max(20, Math.ceil(totalClicks * 0.01))
	const eligible = rows.filter((r) => r.clicks >= threshold)
	if (!eligible.length) return null
	const top = eligible.reduce((best, r) => (r.badRate > best.badRate ? r : best))
	return top.badRate > 0 ? top : null
}

export const rateFindingFrom = (rows: FindingRow[]): string | null => {
	const top = highestRateFindingRow(rows)
	if (!top) return null
	return `${cleanReportText(top.label)} shows the highest bad traffic rate at ${top.badRate.toFixed(1)}% of ${top.clicks.toLocaleString()} clicks.`
}

export const sourceFindingFrom = (rows: FindingRow[], duplicateLabel?: string): string | null => {
	if (!rows.length) return null
	const top = rows.reduce((best, r) => (r.badClicks > best.badClicks ? r : best))
	if (top.badClicks <= 0 || top.label === duplicateLabel) return null
	return `${cleanReportText(top.label)} has the most bad clicks (${top.badClicks.toLocaleString()}).`
}

const changeMagnitude = (change: string): number => {
	const match = change.replace(/,/g, '').match(/[+-]?(?:\d+(?:\.\d*)?|\.\d+)/)
	const n = match ? Number(match[0]) : NaN
	return Number.isFinite(n) ? Math.abs(n) : 0
}

// Metric deltas come in different units ("14.3%", "$1,200", "11s", "-25"), and
// magnitudes across units are not comparable — a raw-magnitude max would let a
// $1,200 delta outrank a 14.3% one just because 1200 > 14. So the mover is
// chosen within a single unit class, preferring percentage (relative) changes,
// which are the most comparable and the most report-worthy movement.
const CHANGE_UNIT_PRIORITY = ['%', '$', 'time', 'count'] as const
const changeUnit = (change: string): (typeof CHANGE_UNIT_PRIORITY)[number] => {
	if (change.includes('%')) return '%'
	if (/[$€£]/.test(change)) return '$'
	if (/\d\s*(?:s|sec|secs|m|min|mins|h|hr|hrs|d|days?)\b/i.test(change)) return 'time'
	return 'count'
}

export const moverFinding = (metrics: PdfMetric[]): string | null => {
	const movers = metrics.filter((m) => m.direction && changeMagnitude(m.change) > 0)
	if (!movers.length) return null
	const unit = CHANGE_UNIT_PRIORITY.find((u) => movers.some((m) => changeUnit(m.change) === u))!
	const comparable = movers.filter((m) => changeUnit(m.change) === unit)
	const top = comparable.reduce((best, m) => (changeMagnitude(m.change) > changeMagnitude(best.change) ? m : best))
	return `${cleanReportText(top.label)} moved ${cleanReportText(top.change)} vs the previous period.`
}

const parseEpoch = (value: string | null): moment.Moment | null => {
	if (!value) return null
	const epoch = Number(value)
	if (!Number.isFinite(epoch)) return null
	const parsed = moment(epoch)
	return parsed.isValid() ? parsed : null
}

export interface BuildOpts {
	state: LayoutSnapshot
	whiteLabel: boolean
	logoDataUri: string | null
	brandColor: string
	chartPngs: Record<string, string | null>
	/** The drawer's editable "Report name". When non-blank it wins over the active
	 *  view's label as the report title, so the schedule name recipients see is the
	 *  title on the download. */
	titleOverride?: string | null
	/** The live chart panel titles, keyed by panel id (see chart-title-snapshot).
	 *  A missing entry falls back to "{Metric} by {Dimension}" off the config. */
	chartTitles?: Record<string, string>
	scope: ReportScope
	/** The grid's live rendered-table snapshot; null falls back to the store-derived table. */
	gridSnapshot: GridPdfSnapshot | null
	/** Injectable for tests; defaults to window.location.search. */
	search?: string
	/** Injectable for tests; defaults to now. */
	now?: Date
}

export function buildReportData({
	state: s,
	whiteLabel,
	logoDataUri,
	brandColor,
	chartPngs,
	titleOverride,
	chartTitles,
	scope,
	gridSnapshot,
	search,
	now
}: BuildOpts): ReportPdfData {
	// Same range logic as GDateRange's "Last 30 days" default: `from`/`to` epoch-ms
	// URL params, else start-of-day 30 days ago through end-of-day today. That
	// spans 31 inclusive dates by design — it must match the app's own picker
	// (g-date-range constants), so the PDF and the on-screen range never disagree.
	const params = new URLSearchParams(search ?? window.location.search)
	const fromParam = params.get('from')
	const toParam = params.get('to')
	let start = parseEpoch(fromParam) ?? moment(now).subtract(30, 'days').startOf('day')
	let end = parseEpoch(toParam) ?? moment(now).endOf('day')
	if (start.isAfter(end)) [start, end] = [end, start]

	const metricDefs: KeyMetric[] = [...new Set(s.metricOrder.length ? s.metricOrder : METRIC_IDS)]
		.filter((id) => !s.hiddenMetrics.includes(id))
		// Resolve each tile through its metricConfig override so the PDF shows the
		// metric the tray actually displays, not the tile id's default metric.
		.map((id) => METRIC_BY_ID[s.metricConfig?.[id]?.metric ?? id] ?? METRIC_BY_ID[id])
		.filter((m): m is KeyMetric => Boolean(m))

	// Values come from the live Key metrics tray (cardsPdfSnapshot) or not at all.
	// A metric the tray never published prints an en-dash: this is a customer-facing
	// document, so a missing number must LOOK missing rather than fall back to the
	// tile's placeholder, which is a plausible-looking invented figure.
	const metrics: PdfMetric[] = metricDefs.map((m) => {
		const live = cardsPdfSnapshot.current?.[m.label]
		return {
			label: cleanReportText(m.label),
			value: live ? cleanReportText(live.value) : NO_VALUE,
			// No change pill without a value to change from — and an en-dash here
			// also keeps the metric out of `moverFinding` (magnitude parses to 0).
			change: live ? cleanReportText(live.change) : NO_VALUE,
			direction: live?.direction,
			trend: (live?.trend ?? '') as PdfMetric['trend']
		}
	})

	// White-label glossary reuses the on-page tooltip copy but swaps the vendor
	// for a neutral subject so no definition names ClickGuard. Both apostrophe
	// forms: the tooltips use the typographic one.
	const glossary = metricDefs.map((m) => ({
		term: cleanReportText(m.label),
		definition: whiteLabel ? neutralizeVendor(m.tooltip) : cleanReportText(m.tooltip)
	}))

	const charts: PdfChart[] = visibleChartIds(s).map((id) => {
		const cfg = s.chartConfig[id]
		const live = chartTitles?.[id]
		// The live title carries the data-driven "(Top 10)" suffix and, for a
		// Stacked bar, "and {Stack by}"; the config-derived form is the fallback.
		const stacked = cfg?.chartType === 'Stacked bar' && cfg.dimension2 && cfg.dimension2 !== cfg.dimension
		const fromCfg =
			cfg?.metric.trim() && cfg.dimension.trim()
				? `${cleanReportText(cfg.metric)} by ${cleanReportText(cfg.dimension)}${
						stacked ? ` and ${cleanReportText(cfg.dimension2 as string)}` : ''
				  }`
				: id
		return {
			id,
			title: live && live.trim() ? cleanReportText(live) : fromCfg,
			png: chartPngs[id] ?? null
		}
	})

	// The live grid is the only source for the table. With no snapshot the section
	// prints its own "no breakdown data" note (see report-pdf) rather than rows the
	// reader would take for their data.
	const table: PdfTable = gridSnapshot
		? {
				dimensionLabel: cleanReportText(gridSnapshot.dimensionLabel),
				columns: [gridSnapshot.dimensionLabel, ...gridSnapshot.columns].map(cleanReportText),
				rows: gridSnapshot.rows.map((r) => ({ depth: r.depth, cells: [r.label, ...r.cells].map(cleanReportText) }))
			}
		: { dimensionLabel: '', columns: [], rows: [] }

	// Highlights quote real per-row values (live grid / render snapshot) or are
	// omitted. `findingRows` is already post search/filter/view, so a row filtered
	// off the table can never be named in the copy.
	const realFindingRows = gridSnapshot?.findingRows ?? []
	const rateLeader = highestRateFindingRow(realFindingRows)
	const findings = [
		rateFindingFrom(realFindingRows),
		sourceFindingFrom(realFindingRows, rateLeader?.label),
		moverFinding(metrics)
	].filter((f): f is string => f !== null)

	// Only metrics with a real value join the pool: an en-dash reads as a number in
	// prose ("flagged – ad clicks as invalid"), so a valueless metric has to drop
	// its clause instead. With none left the builder falls back to the plain
	// "This report covers …" line.
	const summaryPool: SummaryPool = {}
	for (const metric of metrics) {
		if (
			metric.value !== NO_VALUE &&
			(metric.label === 'Analyzed ad clicks' ||
				metric.label === 'Bad ad clicks' ||
				metric.label === 'Blocked sources' ||
				metric.label === 'Prevented ad waste')
		) {
			summaryPool[metric.label] = { value: metric.value }
		}
	}
	const activeTitle = s.savedViews.find((v) => v.id === s.activeViewId)?.label.trim()

	return {
		title:
			titleOverride && titleOverride.trim()
				? cleanReportText(titleOverride)
				: activeTitle
					? cleanReportText(activeTitle)
					: 'Protection Analytics',
		dateRange: `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`,
		generatedAt: moment(now).format('MMM D, YYYY'),
		whiteLabel,
		logoDataUri,
		brandColor,
		metricSection: cleanReportText(s.sectionNames.metric) || 'Key metrics',
		chartSection: cleanReportText(s.sectionNames.chart) || 'Trends & breakdowns',
		preparedBy: whiteLabel ? null : 'ClickGuard',
		scopeLine: buildScopeLine(scope),
		// White-label reports keep the vendor out of the client-facing summary,
		// and stay off the brand too ('the system', not 'ACME analyzed') so the
		// same report reads correctly for the agency and for the agency's client.
		summary: buildSummaryLine(
			start.format('MMM D, YYYY'),
			end.format('MMM D, YYYY'),
			summaryPool,
			whiteLabel ? 'the system' : 'ClickGuard'
		),
		findings,
		glossary,
		metrics,
		charts,
		table
	}
}
